import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FaLocationDot } from "react-icons/fa6";

import MapImg from "../../images/map.png";
import { Input } from "../Input";
import { Button } from "../Button";
import Swal from "sweetalert2";
import { Label } from "../Label";
import axios from "axios";

const Contactus = () => {
  const { t, i18n } = useTranslation();

  const plans = [
    {
      name: t("plans.plan1.name"),
      desc: t("plans.plan1.desc"),
      price: t("plans.plan1.price"),
      features: t("plans.plan1.features", { returnObjects: true }) as string[],
    },
    {
      name: t("plans.plan2.name"),
      desc: t("plans.plan2.desc"),
      price: t("plans.plan2.price"),
      features: t("plans.plan2.features", { returnObjects: true }) as string[],
    },
    {
      name: t("plans.plan3.name"),
      desc: t("plans.plan3.desc"),
      price: t("plans.plan3.price"),
      features: t("plans.plan3.features", { returnObjects: true }) as string[],
    },
  ];

  const formik = useFormik({
    initialValues: {
      companyName: "",
      phone: "",
      region: "",
      pointsNumber: "",
      crNumber: "",
      plan: "",
      message: "",
    },
    validationSchema: Yup.object({
      companyName: Yup.string().required(
        i18n.language === "ar" ? "مطلوب" : "Required"
      ),
      phone: Yup.string()
        .matches(/^05\d{8}$/, t("common.phone_validation"))
        .required(i18n.language === "ar" ? "مطلوب" : "Required"),
      region: Yup.string().required(
        i18n.language === "ar" ? "مطلوب" : "Required"
      ),
      pointsNumber: Yup.number().required(
        i18n.language === "ar" ? "مطلوب" : "Required"
      ),
      crNumber: Yup.string().required(
        i18n.language === "ar" ? "مطلوب" : "Required"
      ),
      plan: Yup.string().required(
        i18n.language === "ar" ? "مطلوب" : "Required"
      ),
      message: Yup.string(),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await axios.post("/request", values);

        if (res.status === 200) {
          resetForm();
          Swal.fire({
            title: t("common.successTitle"),
            text: t("common.successText"),
            icon: "success",
            confirmButtonText: t("common.successButton"),
            confirmButtonColor: "#21D868",
          });
        } else {
          Swal.fire({
            title: t("common.failedTitle"),
            text: t("common.failedText"),
            icon: "error",
            confirmButtonText: t("common.failedButton"),
            confirmButtonColor: "#dc2626",
          });
        }
      } catch (error) {
        Swal.fire({
          title: t("common.failedTitle"),
          text: t("common.failedText"),
          icon: "error",
          confirmButtonText: t("common.failedButton"),
          confirmButtonColor: "#dc2626",
        });
      }
    },
  });

  return (
    <section id="contactus" className="bg-surface">
      <div className="relative w-full container mx-auto py-10 md:py-20 flex flex-col justify-center items-center gap-20 px-4 md:px-0">
        <p className="text-3xl md:text-5xl text-black font-bold">
          {t("contactus.title")}
        </p>
        <div className="w-full flex flex-col md:flex-row gap-10 px-4 md:px-0">
          <div className="w-full md:w-1/2 h-60 md:h-fit relative rounded-xl">
            <img
              src={MapImg}
              className="w-full h-full object-cover"
              alt="Map"
            />
            <FaLocationDot className="text-primary text-6xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>

          <div className="w-full md:w-1/2">
            <form
              onSubmit={formik.handleSubmit}
              className="w-full flex flex-col gap-4 transition-all"
            >
              <div className="w-full space-y-2">
                <label className="text-sm text-black font-semibold">
                  {t("contactus.company")}
                </label>
                <Input
                  id="companyName"
                  type="text"
                  className={`w-full h-10 border border-black/30 px-3 text-gray-600 font-medium cursor-auto ${
                    i18n.language === "ar" ? "text-right" : "text-left"
                  }`}
                  dir={i18n.language === "ar" ? "rtl" : "ltr"}
                  {...formik.getFieldProps("companyName")}
                  error={
                    formik.touched.companyName ? formik.errors.companyName : ""
                  }
                />
              </div>

              <div className="w-full space-y-2">
                <label className="text-sm text-black font-semibold">
                  {t("contactus.phone")}
                </label>
                <div className="w-full flex ltr:flex-row-reverse gap-2">
                  <div className="w-full space-y-1">
                    <Input
                      id="phone"
                      type="text"
                      className="w-full h-10 border border-black/30 px-3 text-gray-600 font-medium cursor-auto"
                      {...formik.getFieldProps("phone")}
                      error={formik.touched.phone ? formik.errors.phone : ""}
                    />
                  </div>
                  <Input
                    id="prefix-phone"
                    type="text"
                    value={i18n.language === "ar" ? "966+" : "+966"}
                    disabled
                    dir={i18n.language === "ar" ? "rtl" : "ltr"}
                    className="w-16 h-10 text-center border border-black/30 px-3 text-black/30 font-medium select-none"
                  />
                </div>
              </div>

              <div className="w-full space-y-2">
                <label className="text-sm text-black font-semibold">
                  {t("contactus.region")}
                </label>
                <Input
                  id="region"
                  type="text"
                  className={`w-full h-10 border border-black/30 px-3 text-gray-600 font-medium cursor-auto ${
                    i18n.language === "ar" ? "text-right" : "text-left"
                  }`}
                  dir={i18n.language === "ar" ? "rtl" : "ltr"}
                  {...formik.getFieldProps("region")}
                  error={formik.touched.region ? formik.errors.region : ""}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="w-full space-y-2">
                  <label className="text-sm text-black font-semibold">
                    {t("contactus.cr_number")}
                  </label>
                  <Input
                    id="crNumber"
                    type="text"
                    className={`w-full h-10 border border-black/30 px-3 text-gray-600 font-medium cursor-auto ${
                      i18n.language === "ar" ? "text-right" : "text-left"
                    }`}
                    dir={i18n.language === "ar" ? "rtl" : "ltr"}
                    {...formik.getFieldProps("crNumber")}
                    error={
                      formik.touched.crNumber ? formik.errors.crNumber : ""
                    }
                  />
                </div>

                <div className="w-full space-y-2">
                  <label className="text-sm text-black font-semibold">
                    {t("contactus.points_of_sale")}
                  </label>
                  <Input
                    id="pointsNumber"
                    type="number"
                    min={0}
                    className={`w-full h-10 border border-black/30 px-3 text-gray-600 font-medium cursor-auto ${
                      i18n.language === "ar" ? "text-right" : "text-left"
                    }`}
                    dir={i18n.language === "ar" ? "rtl" : "ltr"}
                    {...formik.getFieldProps("pointsNumber")}
                    error={
                      formik.touched.pointsNumber
                        ? formik.errors.pointsNumber
                        : ""
                    }
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1 text-sm">
                <Label htmlFor="plan">{t("plans.select_title")}</Label>
                <select
                  className={`border-gray-300 p-3 rounded-md bg-gray-50 text-gray-900 border border-black/30 focus:border-green-500 focus:ring-1 focus:outline-none focus:ring-green-500 ${
                    formik.touched.plan
                      ? formik.errors.plan
                        ? "border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                        : ""
                      : ""
                  }`}
                  id="plan"
                  {...formik.getFieldProps("plan")}
                >
                  <option disabled value="">
                    {t("plans.select_title")}
                  </option>
                  {plans.map((plan) => (
                    <option value={plan?.name}>{plan?.name}</option>
                  ))}
                </select>
                {
                  <span className="px-1 text-xs  text-red-500">
                    {formik.touched.plan ? formik.errors.plan : ""}
                  </span>
                }
              </div>

              <div className="w-full space-y-2">
                <label className="text-sm text-black font-semibold">
                  {t("contactus.message")}
                </label>
                <textarea
                  id="message"
                  rows={6}
                  className={`w-full text-black resize-none rounded-lg border border-black/30 p-3 font-medium cursor-auto focus:border-green-500 focus:ring-1 focus:outline-none focus:ring-green-500 ${
                    i18n.language === "ar" ? "text-right" : "text-left"
                  }`}
                  dir={i18n.language === "ar" ? "rtl" : "ltr"}
                  {...formik.getFieldProps("message")}
                ></textarea>
              </div>

              <Button
                className="w-full !rounded-full text-md py-2 mt-4 !bg-primary !text-main !font-bold focus:outline-none focus:ring-0 hover:!bg-primary/80"
                type="submit"
                disabled={formik.isSubmitting || !formik.isValid}
                loading={formik.isSubmitting}
              >
                {t("contactus.send")}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contactus;
